/**
 * Thin React lifecycle bridge for the MqttService singleton.
 *
 * Wires the plain-TypeScript MqttService to the React component
 * tree: requests a per-event MQTT JWT, connects, and re-subscribes
 * whenever the active event or faction changes. On unmount, the
 * MQTT connection is torn down and managers are unregistered.
 */

import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useAppDispatch } from "../../components/map/mapSlice";
import { mqttService } from "./MqttService";
import { SituationAwarenessManager } from "./managers/SituationAwarenessManager";
import { MarkersManager } from "./managers/MarkersManager";
import { GraphicsManager } from "./managers/GraphicsManager";
import authService from "../authService";

export default function MqttProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const { eventId } = useParams<{ eventId: string }>();
    const selectedFactionId = useSelector(
        (state: RootState) => state.orbat.selectedFactionId
    );
    const accessToken = useSelector(
        (state: RootState) => state.auth.accessToken
    );
    const dispatch = useAppDispatch();
    const cancelledRef = useRef(false);

    useEffect(() => {
        mqttService.registerManager("sa", new SituationAwarenessManager(dispatch));
        mqttService.registerManager("markers", new MarkersManager(dispatch));
        mqttService.registerManager("graphics", new GraphicsManager(dispatch));
        return () => {
            mqttService.unregisterManager("sa");
            mqttService.unregisterManager("markers");
            mqttService.unregisterManager("graphics");
            mqttService.disconnect();
        };
    }, [dispatch]);

    // Fetch the MQTT JWT, subscribe to this faction's topics, then connect.
    // Cleanup unsubscribes the *previous* faction's topics so we never hold
    // subscriptions to two factions simultaneously across a faction switch.
    // The access-token dependency forces a refresh after the axios refresh
    // interceptor rotates the bearer token.
    useEffect(() => {
        if (!eventId || !selectedFactionId || !accessToken) return;

        cancelledRef.current = false;

        const saTopic = `${eventId}/${selectedFactionId}/sa/#`;
        const markersTopic = `${eventId}/${selectedFactionId}/markers/#`;
        const sharedMarkersTopic = `${eventId}/shared/markers/#`;
        const graphicsTopic = `${eventId}/${selectedFactionId}/graphics/#`;
        const sharedGraphicsTopic = `${eventId}/shared/graphics/#`;

        // Register the token-provider callback so MqttService can refresh
        // the MQTT JWT on disconnect (the JWT has a 2h TTL; without this,
        // mqtt.js auto-reconnect would re-present the expired token and
        // cycle "reconnecting → fails" forever).
        mqttService.setTokenProvider(async () => {
            const response = await authService.requestMqttToken(
                eventId,
                selectedFactionId
            );
            return {
                url: response.broker_ws_url,
                token: response.mqtt_token,
            };
        });

        const connectWithToken = async () => {
            try {
                const response = await authService.requestMqttToken(
                    eventId,
                    selectedFactionId
                );
                if (cancelledRef.current) return;
                mqttService.subscribe(saTopic);
                mqttService.subscribe(markersTopic);
                mqttService.subscribe(sharedMarkersTopic);
                mqttService.subscribe(graphicsTopic);
                mqttService.subscribe(sharedGraphicsTopic);
                mqttService.reconnect(
                    response.broker_ws_url,
                    response.mqtt_token
                );
            } catch (error) {
                console.error(
                    "[MQTT] Failed to obtain MQTT token for event=%s faction=%s:",
                    eventId,
                    selectedFactionId,
                    error
                );
            }
        };

        connectWithToken();

        return () => {
            cancelledRef.current = true;
            mqttService.unsubscribe(saTopic);
            mqttService.unsubscribe(markersTopic);
            mqttService.unsubscribe(sharedMarkersTopic);
            mqttService.unsubscribe(graphicsTopic);
            mqttService.unsubscribe(sharedGraphicsTopic);
            // Leave the tokenProvider registered until unmount — a
            // disconnect during a faction switch shouldn't drop the
            // ability to reconnect. The outer effect's cleanup handles
            // final teardown.
        };
    }, [eventId, selectedFactionId, accessToken]);

    // Clear the tokenProvider on unmount so a fully-torn-down MqttService
    // doesn't accidentally try to reconnect using a stale closure.
    useEffect(() => {
        return () => {
            mqttService.setTokenProvider(null);
        };
    }, []);

    return <>{children}</>;
}
